import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function IdentityComplete() {
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkStatus = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/api/payments/identity/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.identity_verified) {
            setStatus('verified');
            if (refreshUser) await refreshUser();
          } else if (attempts < 10) {
            // Stripe Identity can take a moment to process
            setTimeout(() => setAttempts(a => a + 1), 3000);
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    };

    checkStatus();
  }, [token, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4" data-testid="identity-complete-page">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
              <h2 className="text-lg font-semibold">Checking verification status...</h2>
              <p className="text-sm text-muted-foreground">This may take a moment while Stripe processes your documents.</p>
            </>
          )}

          {status === 'verified' && (
            <>
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto" />
              <h2 className="text-lg font-semibold text-green-700">Identity Verified</h2>
              <Badge className="bg-green-100 text-green-700">
                <ShieldCheck className="w-3 h-3 mr-1" /> Verified
              </Badge>
              <p className="text-sm text-muted-foreground">
                Your identity has been successfully verified. A verified badge now appears on your profile.
              </p>
              <Button onClick={() => navigate('/settings')} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Settings
              </Button>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-14 h-14 text-amber-500 mx-auto" />
              <h2 className="text-lg font-semibold text-amber-700">Verification Processing</h2>
              <p className="text-sm text-muted-foreground">
                Your documents are being reviewed by Stripe. This usually takes a few minutes. 
                Check back in your Settings page — we'll update your status automatically.
              </p>
              <Button variant="outline" onClick={() => navigate('/settings')} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Settings
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-14 h-14 text-red-400 mx-auto" />
              <h2 className="text-lg font-semibold">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                We couldn't check your verification status. Please try again from your Settings page.
              </p>
              <Button variant="outline" onClick={() => navigate('/settings')} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Settings
              </Button>
            </>
          )}

          <p className="text-[11px] text-muted-foreground mt-6 pt-4 border-t">
            Your documents are processed securely by Stripe — we never see or store your personal ID or card data.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
